/**
 * **Vue de secours** sans WebGL2 (A58 : le seul repli depuis la v0.22.0, la
 * vue 2D PixiJS ayant disparu). Un canvas 2D à la charte F1 « Pierre taillée »
 * dessine l'ennemi courant ; un toucher ou un swipe vers le haut (A22, mêmes
 * seuils que la 3D — `input.ts`) lance **immédiatement** (`rollThrow` +
 * `applyThrow`), conformément au contrat `BattleView` (docs/MVP_DESIGN.md §8).
 *
 * Elle porte sa propre boucle de chronos, comme la vraie vue de combat :
 * `main.ts` ne fait jamais avancer `game.tick()` lui-même (docs/DECISIONS.md
 * §D, UI). Les lancers du **Larbin** (A18, événement `minionThrow`) sont
 * appliqués tels quels, et le geste manuel est ignoré tant qu'il est actif.
 *
 * Montée par `src/main.ts` quand WebGL2 manque, ou par la vue 3D elle-même
 * quand son pipeline échoue à l'initialisation (nuanceur refusé par un pilote).
 */
import type { Game } from '../core/game';
import { minionEnabledOf, throwResultOf } from './format';
import { gesturePoint, isThrowGesture, pointerDistance, type SwipePoint } from './input';
import { getUiScale } from '../ui/columnScale';
import { currentSpeed } from '../ui/debug/speed';
import { splitGameDt } from './loop';
import type { BattleView, BattleViewAudio } from './view';

export interface StubViewOptions {
  /** Ligne d'explication sous l'invite (ex. « WebGL2 indisponible »). */
  reason?: string;
}

export function createStubBattleView(game: Game, opts: StubViewOptions = {}): BattleView & BattleViewAudio {
  let container: HTMLElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let muted = false;
  let start: SwipePoint | null = null;
  let travel = 0;
  let unsubs: Array<() => void> = [];
  let rafId: number | null = null;
  let lastT = 0;

  function loopFrame(now: number): void {
    if (typeof document !== 'undefined' && document.hidden) {
      lastT = 0;
      rafId = requestAnimationFrame(loopFrame);
      return;
    }
    if (lastT === 0) {
      lastT = now;
      rafId = requestAnimationFrame(loopFrame);
      return;
    }
    const dt = Math.min(0.05, Math.max(0, (now - lastT) / 1000));
    lastT = now;
    // A99 (debug) : même facteur de vitesse que la vue 3D, en combat seulement,
    // découpé en pas de 50 ms de jeu au plus.
    const phase = game.state.phase;
    const factor = phase === 'expedition' || phase === 'invasion' ? currentSpeed() : 1;
    const { count, step } = splitGameDt(dt * factor);
    for (let i = 0; i < count; i++) game.tick(step);
    draw();
    rafId = requestAnimationFrame(loopFrame);
  }

  /**
   * Charte F1 (v0.16.0) : fond d'ardoise, ennemi en plaque octogonale à arête
   * claire, nom en Cinzel, barre de PV mousse, invite en Alegreya Sans —
   * mêmes couleurs que `src/style.css`.
   */
  function draw(): void {
    if (!canvas) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx2d.fillStyle = '#0c0a0f';
    ctx2d.fillRect(0, 0, w, h);

    const enemy = game.state.enemy;
    if (enemy) {
      const ratio = enemy.maxHp > 0 ? enemy.hp / enemy.maxHp : 0;
      const cx = w / 2;
      const cy = h * 0.38;
      const r = 44;
      const c = 14;
      ctx2d.beginPath();
      ctx2d.moveTo(cx - r + c, cy - r);
      ctx2d.lineTo(cx + r - c, cy - r);
      ctx2d.lineTo(cx + r, cy - r + c);
      ctx2d.lineTo(cx + r, cy + r - c);
      ctx2d.lineTo(cx + r - c, cy + r);
      ctx2d.lineTo(cx - r + c, cy + r);
      ctx2d.lineTo(cx - r, cy + r - c);
      ctx2d.lineTo(cx - r, cy - r + c);
      ctx2d.closePath();
      ctx2d.fillStyle = enemy.isBoss ? '#5c1e1c' : '#2c2733';
      ctx2d.fill();
      ctx2d.lineWidth = 2;
      ctx2d.strokeStyle = enemy.isBoss ? '#c9a24a' : '#857a92';
      ctx2d.stroke();
      ctx2d.fillStyle = '#ece3d0';
      ctx2d.font = '700 15px Cinzel, Georgia, serif';
      ctx2d.textAlign = 'center';
      ctx2d.fillText(enemy.name, cx, cy - r - 12);
      ctx2d.fillStyle = '#050407';
      ctx2d.fillRect(cx - 60, cy + r + 12, 120, 10);
      ctx2d.fillStyle = '#5e9446';
      ctx2d.fillRect(cx - 60, cy + r + 12, 120 * Math.max(0, ratio), 10);
    }

    ctx2d.fillStyle = '#ada292';
    ctx2d.font = '15px "Alegreya Sans", system-ui, sans-serif';
    ctx2d.textAlign = 'center';
    ctx2d.fillText('Touchez pour lancer', w / 2, h * 0.82);
    if (opts.reason) {
      ctx2d.fillStyle = '#857a92';
      ctx2d.font = '12.5px "Alegreya Sans", system-ui, sans-serif';
      ctx2d.fillText(opts.reason, w / 2, h * 0.82 + 22);
    }
  }

  function resize(): void {
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    // Colonne agrandie (phase 2) : le rect est en px écran, le canevas en px de conception.
    const s = getUiScale();
    canvas.width = Math.max(1, Math.floor(rect.width / s));
    canvas.height = Math.max(1, Math.floor(rect.height / s));
    draw();
  }

  function onPointerDown(e: PointerEvent): void {
    start = gesturePoint(e, performance.now(), getUiScale());
    travel = 0;
  }

  function onPointerMove(e: PointerEvent): void {
    if (!start) return;
    travel = Math.max(travel, pointerDistance(start, gesturePoint(e, start.t, getUiScale())));
  }

  function onPointerUp(e: PointerEvent): void {
    const from = start;
    start = null;
    if (!from) return;
    const end: SwipePoint = gesturePoint(e, performance.now(), getUiScale());
    if (!isThrowGesture(from, end, travel)) return;
    const phase = game.state.phase;
    if (phase !== 'expedition' && phase !== 'invasion') return;
    // A18 : quand le Larbin lance, le geste manuel est refusé.
    if (minionEnabledOf(game.state) || !game.canThrow()) return;
    const result = game.rollThrow();
    if (result.id >= 0) game.applyThrow(result);
  }

  /** Lancer du Larbin : aucun dé à animer, il est appliqué aussitôt. */
  function onMinionThrow(payload: unknown): void {
    const result = throwResultOf(payload);
    if (result) game.applyThrow(result);
  }

  return {
    mount(el) {
      container = el;
      el.innerHTML = '';
      canvas = document.createElement('canvas');
      canvas.className = 'battle-stub-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      el.appendChild(canvas);
      el.addEventListener('pointerdown', onPointerDown);
      el.addEventListener('pointermove', onPointerMove);
      el.addEventListener('pointerup', onPointerUp);
      window.addEventListener('resize', resize);
      resize();
      unsubs = [
        game.on('spawn', draw),
        game.on('apply', draw),
        game.on('kill', draw),
        game.on('timer', draw),
        game.on('minionThrow', onMinionThrow),
      ];
    },
    unmount() {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      container?.removeEventListener('pointerdown', onPointerDown);
      container?.removeEventListener('pointermove', onPointerMove);
      container?.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('resize', resize);
      for (const off of unsubs) off();
      unsubs = [];
      if (container) container.innerHTML = '';
      container = null;
      canvas = null;
    },
    start() {
      if (rafId !== null) return;
      // Le conteneur était masqué au montage (canvas de 1 × 1) : on le mesure
      // maintenant qu'il est affiché.
      resize();
      lastT = 0;
      rafId = requestAnimationFrame(loopFrame);
    },
    stop() {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
    },
    setMuted(m) {
      muted = m;
    },
    isMuted() {
      return muted;
    },
    // Télémétrie (phase 3) : la vue de secours ne mesure pas les images par seconde.
    fpsStats() {
      return null;
    },
    gpuName() {
      return null;
    },
  };
}
